import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../util/api";
import {
    Box, Paper, Typography, Chip, Table, TableBody, TableCell,
    TableHead, TableRow, TextField, InputAdornment, Divider,
    IconButton, Stack,
} from "@mui/material";
import FormModal from "../../../component/common/FormModal";
import ActionButton from "../../../component/common/ActionButton";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { notify } from "../../../util/toast";
import { runApiAction } from "../../../util/apiFeedback";

import { hrApi, STATUS_COLOR } from "../api/hrApi";
import { API } from "../../auth/admin/api/endpoints";
import { ds } from "../../../styles/designSystem";

import EmployeeEditModal from "../../auth/admin/modals/EmployeeEditModal";
import FormField from "../../../component/common/FormField";
import TablePaginator, { DEFAULT_PAGE_SIZE, TableSizeSelect } from "../../../component/common/TablePaginator";
import LoadingDots from "../../../component/common/LoadingDots";
import ComboBox from "../../../component/common/ComboBox";
import ResizableTable from "../../../component/common/ResizableTable";

function formatBirthMonthDay(birthDate) {
    if (!birthDate) return "";
    const [, month, day] = String(birthDate).split("-");
    if (!month || !day) return "";
    return `${Number(month)}월 ${Number(day)}일`;
}

function isPastBirthdayThisMonth(birthDate) {
    if (!birthDate) return false;
    const today = new Date();
    const [, month, day] = String(birthDate).split("-");
    return Number(month) === today.getMonth() + 1 && Number(day) < today.getDate();
}

function calcBirthdayDDay(birthDate) {
    if (!birthDate) return "";
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [, month, day] = String(birthDate).split("-");
    const bday = new Date(today.getFullYear(), Number(month) - 1, Number(day));
    const diff = Math.round((bday - today) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "D-0";
    if (diff > 0) return `D-${diff}`;
    return `D+${Math.abs(diff)}`;
}

// ── KPI 카드 ──────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, sub, color, children }) => (
    <Paper sx={{ ...ds.cardPadded, flex: 1, minWidth: 150 }}>
        <Typography sx={{ ...ds.typography.label, mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontSize: 26, fontWeight: 800, color: color || ds.colors.textPrimary, lineHeight: 1.2 }}>
            {value}
        </Typography>
        {sub && <Typography sx={{ ...ds.typography.caption, mt: 0.5 }}>{sub}</Typography>}
        {children}
    </Paper>
);

function formatPhoneNumber(value) {
    const digits = String(value || "").replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

export default function HrEmployeeTab() {
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [hrEmployees, setHrEmployees] = useState([]);
    const [adminEmployees, setAdminEmployees] = useState([]);
    const [employeePermMap, setEmployeePermMap] = useState({});
    const [departments, setDepartments] = useState([]);
    const [positions, setPositions] = useState([]); // 직위(직급)
    const [duties, setDuties] = useState([]);        // 직책
    const [permissionDb, setPermissionDb] = useState([]);

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [deptFilter, setDeptFilter] = useState("ALL");
    const [sort, setSort] = useState({ key: "name", dir: "asc" });

    // 서버 페이지네이션
    const [page, setPage]             = useState(1);
    const [pageSize, setPageSize]     = useState(DEFAULT_PAGE_SIZE);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const filterSigRef = useRef("");

    const [statusDlg, setStatusDlg] = useState({ open: false, emp: null, value: "" });
    const [phoneDlg, setPhoneDlg] = useState({ open: false, emp: null, value: "" });

    const [openEdit, setOpenEdit] = useState(false);
    const [editTarget, setEditTarget] = useState(null);

    const codeToLabel = useMemo(() => {
        const map = {};
        for (const p of permissionDb) map[p.codeEnum] = p.permissionNameKr;
        return (code) => map[code] || code;
    }, [permissionDb]);

    // 각 데이터를 독립적으로 로드 — 하나 실패해도 나머지에 영향 없음
    useEffect(() => {
        hrApi.getStats()
            .then(setStats)
            .catch(() => {});
    }, []); // eslint-disable-line

    useEffect(() => {
        api.get(API.departments.list)
            .then(d => setDepartments(Array.isArray(d) ? d : (d?.data || [])))
            .catch(() => {});
    }, []); // eslint-disable-line

    useEffect(() => {
        api.get(API.positions.list)
            .then(d => setPositions(Array.isArray(d) ? d : (d?.data || [])))
            .catch(() => {});
    }, []); // eslint-disable-line

    useEffect(() => {
        api.get(API.duties.list)
            .then(d => setDuties(Array.isArray(d) ? d : (d?.data || [])))
            .catch(() => {});
    }, []); // eslint-disable-line

    useEffect(() => {
        api.get(API.permissions.listDb)
            .then(d => setPermissionDb(Array.isArray(d) ? d : (d?.data || [])))
            .catch(() => {});
    }, []); // eslint-disable-line

    useEffect(() => {
        api.get(API.employees.list)
            .then(d => setAdminEmployees(Array.isArray(d) ? d : (d?.data || [])))
            .catch(() => {});
    }, []); // eslint-disable-line

    const loadPermMap = () => {
        api.get(API.adminPermissions.allEmployees)
            .then(d => {
                const list = Array.isArray(d) ? d : (d?.data || []);
                const map = {};
                for (const item of list) {
                    map[item.employeeCode] = Array.isArray(item.effectivePermissions) ? item.effectivePermissions : [];
                }
                setEmployeePermMap(map);
            })
            .catch(() => {});
    };

    useEffect(() => { loadPermMap(); }, []); // eslint-disable-line

    // 직원 목록 서버 페이지네이션 로드
    const loadEmployees = async (effectivePage) => {
        setLoading(true);
        try {
            const params = {
                page: effectivePage - 1,
                size: pageSize,
                ...(search.trim() ? { search: search.trim() } : {}),
                ...(statusFilter !== "ALL" ? { status: statusFilter } : {}),
                ...(deptFilter !== "ALL" ? { deptId: Number(deptFilter) } : {}),
            };
            const res = await hrApi.getEmployees(params);
            setHrEmployees(res?.content ?? (Array.isArray(res) ? res : []));
            setTotalElements(res?.totalElements ?? 0);
            setTotalPages(res?.totalPages ?? 1);
        } catch {
            setHrEmployees([]);
        } finally {
            setLoading(false);
        }
    };

    const load = () => loadEmployees(page);

    // 필터·페이지 변경 시 재조회
    const filterSig = `${search}|${statusFilter}|${deptFilter}`;
    useEffect(() => {
        const isFilterChange = filterSigRef.current !== filterSig;
        filterSigRef.current = filterSig;
        const effectivePage = isFilterChange ? 1 : page;
        if (isFilterChange) setPage(1);
        loadEmployees(effectivePage);
    }, [page, pageSize, filterSig]); // eslint-disable-line

    const toggleSort = (key) =>
        setSort(s => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

    const sortLabel = (key, label) =>
        `${label}${sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : " ⇅"}`;

    // 현재 페이지 내 클라이언트 정렬
    const filtered = useMemo(() => {
        return [...hrEmployees].sort((a, b) => {
            const av = a[sort.key] ?? "";
            const bv = b[sort.key] ?? "";
            const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv), "ko");
            return sort.dir === "asc" ? cmp : -cmp;
        });
    }, [hrEmployees, sort]);

    // 생일자는 전체 직원(adminEmployees)에서 계산
    const birthdayEmployees = useMemo(() => {
        const month = new Date().getMonth() + 1;
        return adminEmployees
            .filter((employee) => {
                if (!employee.birthDate) return false;
                const [, birthMonth] = String(employee.birthDate).split("-");
                return Number(birthMonth) === month;
            })
            .sort((a, b) => String(a.birthDate).slice(5).localeCompare(String(b.birthDate).slice(5)));
    }, [adminEmployees]);

    const findAdmin = (empCode) => adminEmployees.find(e => e.employeeCode === empCode);

    const handleStatusSave = () => runApiAction({
        request: () => hrApi.updateStatus(statusDlg.emp.id, statusDlg.value),
        onSuccess: () => { setStatusDlg({ open: false, emp: null, value: "" }); load(); },
    });

    const handlePhoneSave = () => runApiAction({
        request: () => hrApi.updatePhone(phoneDlg.emp.id, phoneDlg.value),
        successMsg: "연락처가 저장되었습니다.",
        onSuccess: () => { setPhoneDlg({ open: false, emp: null, value: "" }); load(); },
    });

    const openEmployeeEdit = (e) => {
        setEditTarget(findAdmin(e.employeeCode) || e);
        setOpenEdit(true);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* KPI */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <KpiCard label="전체 직원" value={stats ? stats.total : "—"}
                    sub={`재직 ${stats?.totalActive ?? 0} / 휴직 ${stats?.totalLeave ?? 0} / 퇴사 ${stats?.totalRetired ?? 0}`} />
                <KpiCard label="이달 생일자" value={stats ? `${stats.birthdayThisMonth ?? 0}명` : "—"} color="#2563eb">
                    <Box sx={{
                        mt: 1,
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gap: 0.5,
                    }}>
                        {birthdayEmployees.length === 0 ? (
                            <Typography sx={{ ...ds.typography.caption, gridColumn: "1 / -1" }}>대상자 없음</Typography>
                        ) : (
                            birthdayEmployees.slice(0, 6).map((employee) => {
                                const past = isPastBirthdayThisMonth(employee.birthDate);
                                const color = past ? "#dc2626" : "#16a34a";
                                return (
                                    <Box key={employee.id} sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 0.75,
                                        px: 0.75,
                                        py: 0.45,
                                        borderRadius: 1,
                                        minWidth: 0,
                                    }}>
                                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: ds.colors.textPrimary, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {employee.name}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color, flexShrink: 0, display: "flex", gap: 0.4 }}>
                                        <span>{formatBirthMonthDay(employee.birthDate)}</span>
                                        <span style={{ fontWeight: 700 }}>({calcBirthdayDDay(employee.birthDate)})</span>
                                    </Typography>
                                </Box>
                                );
                            })
                        )}
                        {birthdayEmployees.length > 6 && (
                            <Typography sx={{ fontSize: 11, color: ds.colors.textMuted, gridColumn: "1 / -1" }}>
                                외 {birthdayEmployees.length - 6}명
                            </Typography>
                        )}
                    </Box>
                </KpiCard>
            </Box>

            {/* 직원 테이블 */}
            <Paper sx={ds.card}>
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                    <Typography sx={{ ...ds.typography.sectionTitle, flex: 1 }}>
                        직원 목록 <span style={{ color: ds.colors.textMuted, fontWeight: 400, fontSize: 13 }}>{totalElements}명</span>
                    </Typography>
                    <TextField
                        size="small" placeholder="이름 · 이메일 검색"
                        value={search} onChange={e => setSearch(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
                        sx={{ width: 200, "& .MuiInputBase-root": { height: 36 } }}
                    />
                    <Box sx={{ width: 110 }}>
                        <ComboBox clearable={false} placeholder="전체 상태"
                            options={[
                                { value: "ALL", label: "전체 상태" },
                                { value: "ACTIVE", label: "재직" },
                                { value: "LEAVE", label: "휴직" },
                                { value: "RETIRED", label: "퇴사" },
                            ]}
                            value={statusFilter}
                            onChange={v => setStatusFilter(v)} />
                    </Box>
                    <Box sx={{ width: 120 }}>
                        <ComboBox clearable={false} placeholder="전체 부서"
                            options={[
                                { value: "ALL", label: "전체 부서" },
                                ...departments.map(d => ({ value: String(d.deptId), label: d.deptName })),
                            ]}
                            value={deptFilter}
                            onChange={v => setDeptFilter(v)} />
                    </Box>
                    {/* N개씩 보기 — 표 우측 상단 고정(공용 컨트롤) */}
                    <TableSizeSelect value={pageSize} onChange={v => { setPageSize(v); setPage(1); }} />
                    <ActionButton variant="add"
                        startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                        onClick={() => { setEditTarget(null); setOpenEdit(true); }}
                        sx={{ height: 36 }}
                        label="직원 추가" />
                </Box>
                <Divider />

                {loading ? (
                    <LoadingDots />
                ) : (
                    <>
                    <ResizableTable size="medium" sx={ds.tableSx}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("employeeCode")}>{sortLabel("employeeCode", "사번")}</TableCell>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("name")}>{sortLabel("name", "이름")}</TableCell>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("deptName")}>{sortLabel("deptName", "부서")}</TableCell>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("positionName")}>{sortLabel("positionName", "직위(직급)")}</TableCell>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("dutyName")}>{sortLabel("dutyName", "직책")}</TableCell>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("joinDate")}>{sortLabel("joinDate", "입사일")}</TableCell>
                                <TableCell>연락처</TableCell>
                                <TableCell align="center" sx={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>{sortLabel("status", "상태")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: ds.colors.textMuted }}>
                                        직원이 없습니다.
                                    </TableCell>
                                </TableRow>
                            )}
                            {filtered.map(e => {
                                const sc = STATUS_COLOR[e.status] || STATUS_COLOR.ACTIVE;
                                const eff = employeePermMap[e.employeeCode] || [];
                                return (
                                    <TableRow key={e.id} hover sx={{ cursor: "pointer" }}
                                        onClick={() => openEmployeeEdit(e)}>
                                        <TableCell sx={{ color: ds.colors.textMuted }}>{e.employeeCode}</TableCell>
                                        <TableCell sx={{ width: 170 }}>
                                            <Typography sx={{ fontWeight: 600, lineHeight: 1.15, fontSize: 13 }}>{e.name}</Typography>
                                            <Typography sx={{ fontSize: 12, color: ds.colors.textMuted, mt: 0.15, lineHeight: 1.15 }}>
                                                {e.email || "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ width: 110 }}>{e.deptName || "—"}</TableCell>
                                        <TableCell>{e.positionName || "—"}</TableCell>
                                        <TableCell>{e.dutyName || "—"}</TableCell>
                                        <TableCell sx={{ color: ds.colors.textMuted }}>{e.joinDate || "—"}</TableCell>
                                        <TableCell>
                                            {/*<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>*/}
                                                <Typography sx={{ fontSize: 13 }}>{e.phoneNumber || "—"}</Typography>
                                                {/*<IconButton size="small"*/}
                                                {/*    onClick={(ev) => { ev.stopPropagation(); setPhoneDlg({ open: true, emp: e, value: e.phoneNumber || "" }); }}>*/}
                                                {/*    <EditIcon sx={{ fontSize: 12, color: ds.colors.textMuted }} />*/}
                                                {/*</IconButton>*/}
                                            {/*</Box>*/}
                                        </TableCell>
                                        {/*<TableCell align="center">*/}
                                        {/*    <Typography sx={{ fontSize: 13, fontWeight: 700, color: e.annualRemaining <= 3 ? ds.colors.error : ds.colors.textPrimary }}>*/}
                                        {/*        {e.annualRemaining}일*/}
                                        {/*    </Typography>*/}
                                        {/*    <Typography sx={ds.typography.caption}>*/}
                                        {/*        사용 {e.annualUsed} / {e.annualTotal}*/}
                                        {/*    </Typography>*/}
                                        {/*</TableCell>*/}
                                        <TableCell align="center">
                                            <Chip label={e.statusLabel} size="small"
                                                sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 600, cursor: "pointer" }}
                                                onClick={(ev) => { ev.stopPropagation(); setStatusDlg({ open: true, emp: e, value: e.status }); }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </ResizableTable>
                    <TablePaginator
                        totalPages={totalPages}
                        total={totalElements}
                        pageSize={pageSize}
                        page={page}
                        onPageChange={setPage}
                    />
                    </>
                )}
            </Paper>

            {/* ── 상태 변경 다이얼로그 ── */}
            <FormModal
                open={statusDlg.open}
                onClose={() => setStatusDlg({ open: false, emp: null, value: "" })}
                title={`재직 상태 변경 — ${statusDlg.emp?.name || ""}`}
                maxWidth="xs"
                onSave={handleStatusSave}
            >
                <FormField label="상태" required>
                    <ComboBox clearable={false} placeholder="상태"
                        options={[
                            { value: "ACTIVE", label: "재직" },
                            { value: "LEAVE", label: "휴직" },
                            { value: "RETIRED", label: "퇴사" },
                        ]}
                        value={statusDlg.value}
                        onChange={v => setStatusDlg(p => ({ ...p, value: v }))} />
                </FormField>
            </FormModal>

            {/* ── 전화번호 수정 다이얼로그 ── */}
            <FormModal
                open={phoneDlg.open}
                onClose={() => setPhoneDlg({ open: false, emp: null, value: "" })}
                title={`연락처 수정 — ${phoneDlg.emp?.name || ""}`}
                maxWidth="xs"
                onSave={handlePhoneSave}
            >
                <FormField label="전화번호" required>
                    <TextField fullWidth size="small" value={phoneDlg.value}
                        onChange={e => setPhoneDlg(p => ({ ...p, value: formatPhoneNumber(e.target.value) }))}
                        placeholder="010-1234-5678"
                        inputProps={{ inputMode: "numeric", maxLength: 13 }} />
                </FormField>
            </FormModal>

            {/* ── 직원 편집 모달 (행 클릭 → 수정/권한/삭제/비번초기화) ── */}
            <EmployeeEditModal
                open={openEdit}
                onClose={() => setOpenEdit(false)}
                departments={departments}
                positions={positions}
                duties={duties}
                initial={editTarget}
                onSaved={({ mode, beforeDeptId, afterDeptId }) => {
                    setOpenEdit(false);
                    if (mode === "update" && beforeDeptId !== afterDeptId)
                        notify("부서 변경 → 유효 권한이 즉시 반영됩니다.", "info");
                    load();
                    loadPermMap();
                }}
                onDeleted={() => { setOpenEdit(false); load(); loadPermMap(); }}
            />
        </Box>
    );
}
